import { getRecordValue, isRecord } from '@/lib/utils/type-guards';

/**
 * Lightweight predicate to standardize how we detect Twitter rate limit errors on the client.
 *
 * The Ky client wraps non-OK responses in an Error with:
 *   - name: 'RateLimitError'
 *   - response: Response (status, headers)
 *   - data: parsed JSON body (if any)
 *   - code/status/statusCode hints when available
 * We also handle plain objects emitted by other layers just in case.
 */
export function isRateLimitError(error: unknown): boolean {
  if (!isRecord(error)) {
    return false;
  }

  const nameValue = getRecordValue(error, 'name');
  const name = typeof nameValue === 'string' ? nameValue.toLowerCase() : '';
  if (name.includes('ratelimit')) {
    return true;
  }

  const statusCandidates = [getRecordValue(error, 'status'), getRecordValue(error, 'statusCode')];
  for (const value of statusCandidates) {
    if (typeof value === 'number' && value === 429) {
      return true;
    }
  }

  const response = getRecordValue(error, 'response');
  if (response instanceof Response && response.status === 429) {
    return true;
  }

  const codeValue = getRecordValue(error, 'code');
  const code = typeof codeValue === 'string' ? codeValue.toUpperCase() : undefined;
  if (code === 'RATE_LIMIT' || code === 'TOO_MANY_REQUESTS') {
    return true;
  }

  const data = getRecordValue(error, 'data');
  if (isRecord(data)) {
    const dataCode = getRecordValue(data, 'code');
    if (typeof dataCode === 'string') {
      const normalized = dataCode.toUpperCase();
      if (normalized === 'RATE_LIMIT' || normalized === 'TOO_MANY_REQUESTS') {
        return true;
      }
    }
  }

  return false;
}
